/**
 * Determines whether text should be white or black based on background color
 * @param {string} bgColor - Background color in hexadecimal format
 * @returns {string} - Text color (white or black)
 */
export const getContrastColor = (bgColor) => {
  // Remove # if it exists
  const hexColor = bgColor.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(hexColor.substr(0, 2), 16);
  const g = parseInt(hexColor.substr(2, 2), 16);
  const b = parseInt(hexColor.substr(4, 2), 16);
  
  // Calculate luminance (YIQ formula)
  const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
  
  // Returns white for dark colors and black for light colors
  return (yiq >= 128) ? '#000000' : '#ffffff';
};

/**
 * Lightens or darkens a hexadecimal color
 * @param {string} color - Color in hexadecimal format
 * @param {number} amount - Amount to lighten (positive) or darken (negative)
 * @returns {string} - New color in hexadecimal format
 */
export const lightenDarkenColor = (color, amount) => {
  let usePound = false;
  
  if (color[0] === "#") {
    color = color.slice(1);
    usePound = true;
  }
  
  const num = parseInt(color, 16);
  
  let r = (num >> 16) + amount;
  if (r > 255) r = 255;
  else if (r < 0) r = 0;
  
  let g = ((num >> 8) & 0x00FF) + amount;
  if (g > 255) g = 255;
  else if (g < 0) g = 0;
  
  let b = (num & 0x0000FF) + amount;
  if (b > 255) b = 255;
  else if (b < 0) b = 0;
  
  return (usePound ? "#" : "") + (g | (r << 8) | (b << 16)).toString(16).padStart(6, '0');
};

/**
 * Converts RGB color format to hexadecimal
 * @param {number} r - Red channel value (0-255)
 * @param {number} g - Green channel value (0-255)
 * @param {number} b - Blue channel value (0-255)
 * @returns {string} - Color in hexadecimal format
 */
export const rgbToHex = (r, g, b) => {
  return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

/**
 * Converts hexadecimal color format to RGB
 * @param {string} hex - Color in hexadecimal format
 * @returns {Object} - Object with r, g, b values
 */
export const hexToRgb = (hex) => {
  // Remove # if it exists
  const cleanedHex = hex.replace('#', '');
  
  // Convert to RGB
  const r = parseInt(cleanedHex.substr(0, 2), 16);
  const g = parseInt(cleanedHex.substr(2, 2), 16);
  const b = parseInt(cleanedHex.substr(4, 2), 16);
  
  return { r, g, b };
};